import { Link } from "react-router-dom";
import { ShieldCheck, MapPin, HandHeart, ArrowRight } from "lucide-react";
import { FadeUp, Eyebrow, MaskedLines } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { Seo } from "@/components/Seo";

const VALUES = [
  { icon: ShieldCheck, title: "Integrity", text: "Every shilling collected is accounted for. Every lease signed is honoured. Transparency has been our currency since 1952." },
  { icon: MapPin, title: "Local Expertise", text: "Born in Mombasa, rooted on the coast. We know every street, tide and tenant from the island to Kilifi, Malindi and Diani." },
  { icon: HandHeart, title: "Long-term Value", text: "We manage for decades, not quarters. Families who listed with us in the 1960s still trust us with their grandchildren's assets." },
];

const TEAMS = [
  { title: "Property Management Team", text: "Tenant relations, rent collection, maintenance and facility management across our managed portfolio." },
  { title: "Sales & Leasing Team", text: "Valuations, marketing and negotiations for residential and commercial transactions." },
  { title: "Facilities & Compliance", text: "24/7 building operations, security, statutory compliance and planned maintenance." },
  { title: "Architectural & Design", text: "In-house architectural planning and design — from concept and space planning to the delivery of developments like SBS Markiti Complex." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-28 md:pt-36">
      <Seo
        path="/about"
        title="About Us — A Mombasa Institution Since 1952"
        description="For over 70 years SBS Properties has managed homes, shops and offices across Mombasa and the Kenyan coast — built on integrity, local expertise and long-term value."
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-8 pb-20">
        <Eyebrow>About SBS Properties</Eyebrow>
        <MaskedLines
          className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
          lines={["A Mombasa institution", <em key="e" className="italic text-brand">since 1952.</em>]}
        />
      </div>

      <section className="border-t hairline">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-4">
            <span className="font-serif text-7xl text-brand">70+</span>
            <p className="mt-2 text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground">Years of Unbroken Service</p>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-8 space-y-5 text-muted-foreground leading-relaxed text-base sm:text-lg">
            <p>
              Founded in Mombasa in 1952, SBS Properties has grown alongside the city itself — from managing some of the island's earliest commercial buildings to developing SBS Markiti Complex, one of the coast's most recognisable business addresses.
            </p>
            <p>
              Today the firm manages homes, shops and offices across Mombasa and the Kenyan coast, serving landlords, tenants, buyers and sellers with the same founding principle: <span className="text-foreground font-medium">treat every asset as if it were our own.</span>
            </p>
            <p>
              That is why our client relationships are measured not in lease terms, but in generations.
            </p>
          </FadeUp>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-20 md:py-28">
        <FadeUp>
          <Eyebrow>What Guides Us</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">Our Values</h2>
        </FadeUp>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          {VALUES.map((v, i) => (
            <FadeUp key={v.title} delay={i * 0.1} className="border hairline bg-white p-8">
              <v.icon className="h-8 w-8 text-brand" />
              <h3 className="mt-6 font-serif text-2xl">{v.title}</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{v.text}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="bg-sand border-y hairline">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-20 md:py-28">
          <FadeUp>
            <Eyebrow>The People</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">Our Teams</h2>
          </FadeUp>
          <div className="mt-12">
            {TEAMS.map((t, i) => (
              <FadeUp key={t.title} delay={i * 0.06} className="grid grid-cols-12 gap-6 border-t hairline py-8 items-baseline">
                <span className="col-span-2 sm:col-span-1 font-mono text-sm text-brand font-bold">0{i + 1}</span>
                <h3 className="col-span-10 sm:col-span-4 font-display text-xl font-semibold">{t.title}</h3>
                <p className="col-span-12 sm:col-span-7 text-muted-foreground text-sm leading-relaxed">{t.text}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-20 md:py-28">
        <FadeUp className="text-center">
          <Link to="/contact" data-testid="about-cta-btn" className="group inline-flex items-center gap-2 bg-brand px-8 py-4 text-sm font-semibold text-ivory hover:bg-brand-hover transition-colors">
            Work With Us <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </section>
    </div>
  );
}
